import { cloneDeep, isEqual } from "lodash";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  useForm,
  type Control,
  type DeepPartial,
  type DeepRequired,
  type FieldErrorsImpl,
  type FieldValues,
  type Path,
  type PathValue,
  type Resolver,
  type UseFormGetValues,
  type UseFormHandleSubmit,
  type UseFormSetValue,
} from "react-hook-form";

import {
  type OnDeepPartialSubmitType,
  useGetChangedFieldsOnSubmitHandlerDecorator,
  useGetHandleSubmit,
} from "@ds/hooks/redux-forms-hooks";

import { getProxyFieldPath } from "@ds/utils/entities";
import { normalizeFormationFieldNameBeforeEdit } from "@ds/utils/forms";
import { hasProperty } from "@ds/utils/properties";

import { type EditableDetailsCardRowProps } from "./editable-details-card-row-decorator";

export type EditableDetailsCardChildProps<T extends FieldValues> = {
  isDirty: boolean;
  isValid: boolean;
  entity: T;
  control: Control<T>;
  errors: FieldErrorsImpl<DeepRequired<T>>;
  setValue: UseFormSetValue<T>;
  getValues: UseFormGetValues<T>;
  /** Validates and submits only fields marked dirty in `dirtyFields` (existing details cards). */
  handleSubmit: UseFormHandleSubmit<T>;
  /** RHF’s native `handleSubmit`: runs the resolver on the full form. Use for field arrays / full-form validation. */
  handleSubmitAllFields: UseFormHandleSubmit<T>;
  onResetForm: () => void;
  onSubmitForm: (data: T) => void;
  onCancelEdit: () => void;
  /** Exits edit mode without resetting the form. Use after a whole-card save where `onSubmitForm` was
   *  called directly (e.g. field-array cards) so the card exits edit mode only on valid submission. */
  onSaveAllFields: () => void;
} & EditableDetailsCardRowProps<T>;

type EditableDetailsCardDecoratorBaseProps<T extends FieldValues> = {
  children: (props: EditableDetailsCardChildProps<T>) => JSX.Element;
  initialValue: DeepPartial<T>;
  validationResolver: Resolver<T>;
  /** Holds back updates of `initialValue` (e.g. IoT updates) while the card is edited with unsaved changes, and
   *  applies the latest one when editing ends. */
  shouldKeepUnsavedEdits?: boolean;
};

export type EditableDetailsCardDecoratorProps<T extends FieldValues> =
  | (EditableDetailsCardDecoratorBaseProps<T> & {
      onSubmit: OnDeepPartialSubmitType<T>;
      resolveOnSubmit?: undefined;
    })
  | (EditableDetailsCardDecoratorBaseProps<T> & {
      onSubmit?: undefined;
      /**
       * Alternative to `onSubmit`. Called once (inside the decorator) with RHF's stable `getValues`
       * to produce the actual submit handler — useful for field-array cards that need the full form
       * state on submit, not just dirty fields.
       *
       * **Must be a stable reference** (wrap in `useCallback`) to avoid re-deriving the handler on
       * every render.
       */
      resolveOnSubmit: (getValues: UseFormGetValues<T>) => OnDeepPartialSubmitType<T>;
    });

export const EditableDetailsCardDecorator = <T extends FieldValues>({
  children,
  initialValue,
  validationResolver,
  onSubmit,
  resolveOnSubmit,
  shouldKeepUnsavedEdits = false,
}: EditableDetailsCardDecoratorProps<T>) => {
  const [previousValue, setPreviousValue] = useState<PathValue<T, Path<T>>>(undefined as PathValue<T, Path<T>>);
  const [previousProxyValue, setPreviousProxyValue] = useState<PathValue<T, Path<T>>>(
    undefined as PathValue<T, Path<T>>,
  );

  const [isEditing, setIsEditing] = useState(false);

  const lastSyncedInitialValue = useRef<DeepPartial<T> | undefined>(undefined);

  const {
    control,
    watch,
    setValue,
    getValues,
    trigger,
    reset,
    resetField,
    setFocus,
    handleSubmit: handleSubmitAllFields,
    formState: { isDirty, dirtyFields, errors, isValid },
  } = useForm<T>({
    mode: "all",
    reValidateMode: "onChange",
    defaultValues: initialValue,
    resolver: validationResolver,
  });

  // Sync form when initialValue *data* changes (not referential identity). Aggregated selectors
  // and parent spreads often pass a new object each render; resetting every time breaks useFieldArray.
  const shouldHoldUpdates = shouldKeepUnsavedEdits && isEditing && isDirty;
  useEffect(() => {
    if (shouldHoldUpdates) {
      return;
    }

    if (lastSyncedInitialValue.current === undefined || !isEqual(lastSyncedInitialValue.current, initialValue)) {
      lastSyncedInitialValue.current = cloneDeep(initialValue);
      reset(initialValue);
    }
  }, [initialValue, reset, shouldHoldUpdates]);

  const handleSubmit = useGetHandleSubmit({
    getValues,
    dirtyFields,
    trigger,
    errors,
  });

  const onResetFormClickHandler = useCallback(() => reset(), [reset]);
  const onCancelEditClickHandler = useCallback(() => {
    reset(lastSyncedInitialValue.current ?? initialValue);
    setIsEditing(false);
  }, [initialValue, reset]);

  const resolveOnSubmitRef = useRef(resolveOnSubmit);
  useEffect(() => {
    if (process.env.NODE_ENV !== "production" && resolveOnSubmit !== resolveOnSubmitRef.current) {
      console.warn(
        "EditableDetailsCardDecorator: `resolveOnSubmit` changed identity between renders. " +
          "Wrap it in useCallback to avoid recreating the submit handler every render.",
      );
    }
    resolveOnSubmitRef.current = resolveOnSubmit;
  }, [resolveOnSubmit]);

  const effectiveOnSubmit = useMemo((): OnDeepPartialSubmitType<T> => {
    if (resolveOnSubmit) {
      return resolveOnSubmit(getValues);
    }
    return onSubmit as OnDeepPartialSubmitType<T>;
  }, [resolveOnSubmit, getValues, onSubmit]);

  const onSubmitFormClickHandler = useGetChangedFieldsOnSubmitHandlerDecorator<T>({
    dirtyFields,
    onSubmit: effectiveOnSubmit,
  });

  const onEditRowClickHandler = useCallback(
    (field: Path<T>) => () => {
      const proxyField = getProxyFieldPath(field);
      setPreviousValue(cloneDeep(getValues(field)));
      setPreviousProxyValue(cloneDeep(getValues(proxyField)));
      setIsEditing(true);

      setTimeout(() => {
        try {
          setFocus(normalizeFormationFieldNameBeforeEdit(field), { shouldSelect: true });
        } catch {
          /* here are some issues with primereact accessibility support */
        }
      }, 0);
    },
    [getValues, setFocus],
  );

  const onResetRowClickHandler = useCallback(
    (field: Path<T>) => () => {
      const proxyField = getProxyFieldPath(field);
      resetField(field);
      resetField(proxyField);
    },
    [resetField],
  );

  const onCancelRowClickHandler = useCallback(
    (field: Path<T>) => () => {
      const proxyField = getProxyFieldPath(field);
      setValue(field, previousValue, {
        shouldDirty: true,
        shouldTouch: true,
        shouldValidate: true,
      });

      const data = getValues();
      if (hasProperty(data, proxyField)) {
        setValue(proxyField, previousProxyValue, {
          shouldDirty: true,
          shouldTouch: true,
          shouldValidate: true,
        });
      }

      setIsEditing(false);
    },
    [previousValue, previousProxyValue, setValue, getValues],
  );

  const onSaveRowClickHandler = useCallback((_: Path<T>) => () => setIsEditing(false), []);
  const onSaveAllFieldsClickHandler = useCallback(() => setIsEditing(false), []);

  const entity = watch();
  return children({
    entity,
    isEditing,
    isDirty,
    isValid,
    control,
    dirtyFields,
    errors,
    setValue,
    getValues,
    handleSubmit,
    handleSubmitAllFields,
    onResetForm: onResetFormClickHandler,
    onSubmitForm: onSubmitFormClickHandler,
    onCancelEdit: onCancelEditClickHandler,
    onEditRow: onEditRowClickHandler,
    onResetRow: onResetRowClickHandler,
    onCancelRow: onCancelRowClickHandler,
    onSaveRow: onSaveRowClickHandler,
    onSaveAllFields: onSaveAllFieldsClickHandler,
  });
};
